import { JSX } from 'react';
import {
    JobOrderRawType,
    JobOrderDisplayType,
    JobOrderRegenerateBody,
    Message,
} from 'components/JobGenerator/types';
import { getFormattedFilters } from 'helpers/filters';
import { deleteHtmlFromString, parseHtmlOrNull } from 'helpers/htmlParser';
import { DefaultFiltersType, JobParamsType } from 'store/auth/types';
import { FiltersType, SelectedLanguageType, TargetCountryType } from 'store/global/types';

export const renderValue = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            return null;
        }
        return value.join(', ');
    }

    if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value, null, 2);
    }

    return String(value);
};

/**
 * Format work locations into a readable string
 */
export const formatWorkLocations = (locations: JobOrderRawType['workLocations']): string => {
    if (!locations?.length) {
        return '';
    }
    return locations
        .map((loc) => {
            const { locality, administrativeArea, regionCode } = loc.address;
            const parts = [locality, administrativeArea, regionCode].filter(Boolean);
            return parts.join(', ');
        })
        .join('; ');
};

/**
 * Format pay rates into a readable string - uses the last pay rate entry
 */
export const formatPayRates = (rates: JobOrderRawType['payRates']): string => {
    if (!rates?.length) {
        return '';
    }

    // Use the last pay rate entry
    const lastRate = rates[rates.length - 1];
    const parts = [
        lastRate.amount ? `${lastRate.amount} ${lastRate.currencyCode || ''}` : '',
        lastRate.unit ? `per ${lastRate.unit}` : '',
        lastRate.type ? `(${lastRate.type})` : '',
    ].filter(Boolean);
    return parts.join(' ');
};

/**
 * Format education requirements into a readable string - uses the last education requirement
 */
export const formatEducationRequirements = (
    requirements: JobOrderRawType['educationRequirements'],
): string => {
    if (!requirements?.length) {
        return '';
    }

    // Use the last education requirement entry
    const lastReq = requirements[requirements.length - 1];
    const parts = [
        lastReq.degree?.type || '',
        lastReq.importance ? `(${lastReq.importance})` : '',
    ].filter(Boolean);
    return parts.join(' ');
};

/**
 * Format job requirements into a readable string - uses the last job requirement
 */
export const formatJobRequirements = (requirements: JobOrderRawType['jobRequirements']): string => {
    if (!requirements?.length) {
        return '';
    }

    // Use the last job requirement entry
    const lastReq = requirements[requirements.length - 1];
    const parts = [
        lastReq.jobRequirementType || '',
        lastReq.context ? `- ${lastReq.context}` : '',
        lastReq.importance ? `(${lastReq.importance})` : '',
    ].filter(Boolean);
    return parts.join(' ');
};

/**
 * Extract a specific field from job descriptions array
 */
export const getFieldFromJobDescriptions = (
    rawData: JobOrderRawType | null,
    field: 'description' | 'qualifications' | 'responsibilities',
): string => {
    if (!rawData) {
        return '';
    }

    if (!rawData.jobDescriptions?.length) {
        return '';
    }

    if (!rawData[field]) {
        return '';
    }

    // Filter job descriptions that have the requested field, then get the last one
    const validDescriptions = rawData.jobDescriptions.filter(
        (desc) => desc[field] && desc[field].trim(),
    );

    if (validDescriptions.length > 0) {
        const lastJobDesc = validDescriptions[validDescriptions.length - 1];
        return lastJobDesc[field];
    }

    return '';
};

/**
 * Transform raw job order data to display format
 */
export const transformToDisplayData = (rawData: JobOrderRawType): JobOrderDisplayType => {
    return {
        job_title: renderValue(rawData.title),
        country: renderValue(rawData.country),
        brand: renderValue(rawData.brand),
        language: renderValue(rawData.language),
        description: parseHtmlOrNull(
            renderValue(getFieldFromJobDescriptions(rawData, 'description') || rawData.description),
        ),
        responsibilities: parseHtmlOrNull(
            renderValue(
                getFieldFromJobDescriptions(rawData, 'responsibilities') ||
                    rawData.responsibilities,
            ),
        ),
        qualifications: parseHtmlOrNull(
            renderValue(
                getFieldFromJobDescriptions(rawData, 'qualifications') || rawData.qualifications,
            ),
        ),
        remote_work_allowed: renderValue(rawData.remoteWorkAllowed),
        employment_categories: renderValue(rawData.employmentCategories),
        expected_hours_of_service: renderValue(rawData.expectedHoursOfService),
        work_locations: renderValue(formatWorkLocations(rawData.workLocations)),
        pay_rates: renderValue(formatPayRates(rawData.payRates)),
        education_requirements: renderValue(
            formatEducationRequirements(rawData.educationRequirements),
        ),
        reason_for_opening: renderValue(rawData.reasonForOpening),
        job_requirements: renderValue(formatJobRequirements(rawData.jobRequirements)),
        job_level: renderValue(rawData.jobLevel),
    };
};

/**
 * Extract clean field from job descriptions for API requests
 */
export const getCleanFieldFromJobDescriptions = (
    rawData: JobOrderRawType | null,
    field: 'description' | 'qualifications' | 'responsibilities',
): string => {
    const fieldValue = getFieldFromJobDescriptions(rawData, field);
    return fieldValue ? deleteHtmlFromString(fieldValue).trim() : '';
};

/**
 * Find country code
 */
export const getCountryCode = (targetCountries: TargetCountryType[], country: string) => {
    return targetCountries.find(
        (countryItem: { title: string; country_code: string }) => countryItem.title === country,
    )?.country_code;
};

/*
 * Parse ROneFlag
 */
export const getROneFlag = (
    lastMessage: Message | undefined,
    isSectionsModeEnabled: boolean,
    jobOrderData: JobOrderRawType | null,
) => {
    return lastMessage
        ? (lastMessage.r1_flag ?? false)
        : isSectionsModeEnabled || jobOrderData?.r1_flag || false;
};

/*
 * Parse Parameters
 */
export const parseParameters = (
    params: JobParamsType | undefined,
    getFilters: () => DefaultFiltersType,
    additionalSettings: FiltersType,
    countryCode: string | undefined,
): Record<string, unknown> | undefined => {
    if (params) {
        return params as unknown as Record<string, unknown>;
    }
    return getFormattedFilters(getFilters(), additionalSettings, countryCode);
};

/**
 * Create body for Regenerate request
 */
export const createRegenerateRequestBody = (
    document_id: string | undefined,
    prev_message_id: string | null | undefined,
    language: SelectedLanguageType,
    jobOrderData: JobOrderRawType | null,
    brand: string | undefined,
    intake_notes: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parameters: Record<string, any> | undefined,
    use_signature: boolean,
    rOneFlag: boolean,
): JobOrderRegenerateBody => {
    const requestBody = {
        conversation_id: document_id as string,
        job_order_id: jobOrderData?.job_order_id || '',
        prev_message_id: prev_message_id || undefined,
        intake_notes: intake_notes,
        language: language.language_code,
        country: language?.country || jobOrderData?.country || '',
        brand: brand || jobOrderData?.brand || '',
        parameters: parameters,
        use_signature: use_signature,
        title: jobOrderData?.title || '',
        description: getCleanFieldFromJobDescriptions(jobOrderData, 'description'),
        responsibilities: getCleanFieldFromJobDescriptions(jobOrderData, 'responsibilities'),
        qualifications: getCleanFieldFromJobDescriptions(jobOrderData, 'qualifications'),
        r1_flag: rOneFlag,
        remoteWorkAllowed: jobOrderData?.remoteWorkAllowed || false,
        employmentCategories: jobOrderData?.employmentCategories || [],
        expectedHoursOfService: jobOrderData?.expectedHoursOfService || [],
        workLocations: jobOrderData?.workLocations || [],
        payRates: jobOrderData?.payRates || [],
    };

    if (!requestBody.prev_message_id) {
        delete requestBody.prev_message_id;
    }

    if (!requestBody.parameters) {
        delete requestBody.parameters;
    }

    return requestBody;
};

export const isContentLong = (
    key: string,
    value: string | JSX.Element | JSX.Element[] | null,
    longFormFields: string[],
) => {
    if (value === null) {
        return false;
    }

    if (longFormFields.includes(key)) {
        return true;
    }

    if (typeof value !== 'string') {
        return true;
    }

    // Check for multiline content
    if (value && value.includes('\n')) {
        return true;
    }

    // Check if the value alone is too long for a column (roughly 30-40 chars per column)
    const valueLength = value ? value.length : 0;
    return valueLength > 40;
};

export const getInfoBoxEntries = (
    data: Record<string, string | JSX.Element | JSX.Element[] | null>,
) => {
    // Split entries into two-column and single-column layouts
    // If container is narrow, force everything to single column
    const longFormFields = ['Description', 'Responsibilities', 'Qualifications'];
    const twoColumnEntries = Object.entries(data).filter(
        ([key, value]) => !isContentLong(key, value, longFormFields),
    );
    const singleColumnEntries = Object.entries(data).filter(([key, value]) =>
        isContentLong(key, value, longFormFields),
    );

    return { twoColumnEntries, singleColumnEntries };
};

export const splitDisplayJobIntoColumns = (
    isNarrow: boolean,
    twoColumnEntries: [string, string | JSX.Element | JSX.Element[] | null][],
    singleColumnEntries: [string, string | JSX.Element | JSX.Element[] | null][],
) => {
    // If isNarrow, combine all entries into single column layout
    const displayTwoCol = isNarrow ? [] : twoColumnEntries;
    const displaySingleCol = isNarrow
        ? [...twoColumnEntries, ...singleColumnEntries]
        : singleColumnEntries;

    // Split two-column entries into left and right columns
    const left = displayTwoCol.filter((_, index) => index % 2 === 0);
    const right = displayTwoCol.filter((_, index) => index % 2 === 1);

    return {
        displayTwoColumnEntries: displayTwoCol,
        displaySingleColumnEntries: displaySingleCol,
        leftColumn: left,
        rightColumn: right,
    };
};

export const parseJobOrderRaw = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>,
    _job_order_id: string | null = null,
): JobOrderRawType => {
    const parsed = {
        job_order_id: _job_order_id || '',
        language: data?.language || '',
        country: data?.country || '',
        brand: data?.brand || '',
        title: '',
        description: data?.description || '',
        responsibilities: data?.responsibilities || '',
        qualifications: data?.qualifications || '',
        r1_flag: data?.r1_flag || false,
        remoteWorkAllowed: data?.remoteWorkAllowed || false,
        employmentCategories: data?.employmentCategories || [],
        expectedHoursOfService: data?.expectedHoursOfService || [],
        workLocations: data?.workLocations || [],
        payRates: data?.payRates || [],
        educationRequirements: data?.educationRequirements || [],
        reasonForOpening: data?.reasonForOpening || '',
        jobRequirements: data?.jobRequirements || [],
        jobDescriptions: data?.jobDescriptions || [],
        jobLevel: data?.jobLevel || '',
    };

    const { jobDescriptions, jobTitles } = data;
    if (jobTitles && Array.isArray(jobTitles)) {
        const clientTitle =
            jobTitles.find((title: { type: string; title: string }) => title.type === 'Client')
                ?.title || '';
        parsed.title = clientTitle;
    }

    if (jobDescriptions && Array.isArray(jobDescriptions) && jobDescriptions.length > 0) {
        const firstDesc = jobDescriptions[0];
        parsed.description = firstDesc.description ?? '';
        parsed.responsibilities = firstDesc?.responsibilities ?? '';
        parsed.qualifications = firstDesc?.qualifications ?? '';
    }

    return parsed;
};
